import Hls from "hls.js";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import ReplayPauseIcon from "../../assets/icons/replay-pause.svg";
import ReplayPlayIcon from "../../assets/icons/replay-play.svg";
import "./index.less";

interface VideoLiveReplaysPlayerProps {
  videoUrl: string;
  onFullscreen?: () => void;
  onControlsVisibilityChange?: (visible: boolean) => void;
}

export interface VideoLiveReplaysPlayerRef {
  seekTo: (seconds: number) => void;
}

const VideoLiveReplaysPlayer = forwardRef<VideoLiveReplaysPlayerRef, VideoLiveReplaysPlayerProps>(
  ({ videoUrl, onFullscreen, onControlsVisibilityChange }, ref) => {
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [progress, setProgress] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [showControls, setShowControls] = useState<boolean>(true);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isPlayingRef = useRef<boolean>(false);

    // Expose seekTo method to parent component
    useImperativeHandle(ref, () => ({
      seekTo: (seconds: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = seconds;
          setCurrentTime(seconds);
          const newProgress = (seconds / duration) * 100;
          setProgress(newProgress);
        }
      },
    }));

    // Format time for display
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    const showControlsTemporarily = () => {
      setShowControls(true);

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }

      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlayingRef.current) {
          setShowControls(false);
        }
      }, 3000);
    };

    const handleVideoClick = () => {
      if (!showControls) {
        showControlsTemporarily();
      }
    };

    const togglePlay = (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }

      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current
            .play()
            .then(() => {
              setIsPlaying(true);
              showControlsTemporarily();
            })
            .catch((error) => console.error("Error playing video:", error));
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
          setShowControls(true);
          if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
          }
        }
      }
    };

    const updateProgress = () => {
      if (videoRef.current && !isDragging) {
        const currentTime = videoRef.current.currentTime;
        const duration = videoRef.current.duration;
        const progress = (currentTime / duration) * 100;
        setProgress(progress);
        setCurrentTime(currentTime);
        setDuration(duration);
      }
    };

    const seekToPosition = (percentage: number) => {
      if (!videoRef.current || !duration) return;

      const seekTime = (percentage / 100) * duration;
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
      setProgress(percentage);
    };

    const seekVideo = (e: React.MouseEvent<HTMLDivElement>) => {
      if (progressBarRef.current && videoRef.current) {
        const rect = progressBarRef.current.getBoundingClientRect();
        const percentage = ((e.clientX - rect.left) / rect.width) * 100;
        seekToPosition(Math.max(0, Math.min(100, percentage)));
      }
    };

    // Touch handlers for mobile
    const handleNativeTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);

      if (!progressBarRef.current || !duration) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      const percentage = (touchX / rect.width) * 100;

      seekToPosition(Math.max(0, Math.min(100, percentage)));
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      if (!isDragging || !progressBarRef.current || !duration) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = progressBarRef.current.getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      const percentage = (touchX / rect.width) * 100;

      seekToPosition(Math.max(0, Math.min(100, percentage)));
    };

    const handleNativeTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(false);
    };

    // Mouse drag handlers for desktop
    const handleProgressMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragging(true);

      const handleMouseMove = (event: MouseEvent) => {
        if (!progressBarRef.current || !duration) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const percentage = (mouseX / rect.width) * 100;

        seekToPosition(Math.max(0, Math.min(100, percentage)));
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      // Handle initial click
      if (progressBarRef.current && videoRef.current) {
        const rect = progressBarRef.current.getBoundingClientRect();
        const percentage = ((e.clientX - rect.left) / rect.width) * 100;
        seekToPosition(Math.max(0, Math.min(100, percentage)));
      }
    };

    // Keep isPlayingRef in sync with isPlaying
    useEffect(() => {
      isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    // Notify parent when controls visibility changes
    useEffect(() => {
      if (onControlsVisibilityChange) {
        onControlsVisibilityChange(showControls);
      }
    }, [showControls, onControlsVisibilityChange]);

    useEffect(() => {
      const video = videoRef.current;
      if (video) {
        const handleTimeUpdate = () => updateProgress();
        const handlePlay = () => {
          setIsPlaying(true);
          showControlsTemporarily();
        };
        const handlePause = () => {
          setIsPlaying(false);
          setShowControls(true);
        };
        const handleLoadedMetadata = () => {
          setDuration(video.duration);
          setCurrentTime(video.currentTime);
        };

        video.addEventListener("timeupdate", handleTimeUpdate);
        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("loadedmetadata", handleLoadedMetadata);

        return () => {
          video.removeEventListener("timeupdate", handleTimeUpdate);
          video.removeEventListener("play", handlePlay);
          video.removeEventListener("pause", handlePause);
          video.removeEventListener("loadedmetadata", handleLoadedMetadata);
          if (controlsTimeoutRef.current) {
            clearTimeout(controlsTimeoutRef.current);
          }
        };
      }
    }, []);

    // HLS initialization
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !videoUrl) return;

      // Extract CloudFront signature parameters from the source URL
      let queryParams = "";
      try {
        const srcUrl = new URL(videoUrl, window.location.href);
        queryParams = srcUrl.search.substring(1);
      } catch (e) {
        console.error("[VideoLiveReplaysPlayer] Failed to parse videoUrl:", e);
      }

      const createSignedLoader = () => {
        const DefaultLoader = Hls.DefaultConfig.loader;
        return class SignedLoader extends DefaultLoader {
          constructor(config: any) {
            super(config);
            const originalLoad = this.load.bind(this);
            this.load = (context: any, config: any, callbacks: any) => {
              if (queryParams && context.url) {
                try {
                  const requestUrl = new URL(context.url, window.location.href);
                  const hasSignature =
                    requestUrl.searchParams.has("Policy") && requestUrl.searchParams.has("Key-Pair-Id") && requestUrl.searchParams.has("Signature");
                  if (!hasSignature) {
                    const separator = context.url.includes("?") ? "&" : "?";
                    context.url = `${context.url}${separator}${queryParams}`;
                  }
                } catch (e) {
                  console.error("[SignedLoader] Failed to modify URL:", e);
                }
              }
              originalLoad(context, config, callbacks);
            };
          }
        };
      };

      if (Hls.isSupported()) {
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: false,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          maxBufferSize: 30 * 1000 * 1000,
          maxBufferHole: 0.5,
          manifestLoadingTimeOut: 10000,
          manifestLoadingMaxRetry: 4,
          manifestLoadingRetryDelay: 1000,
          fragLoadingTimeOut: 20000,
          fragLoadingMaxRetry: 6,
          fragLoadingRetryDelay: 1000,
          startLevel: -1,
          autoStartLoad: true,
          loader: createSignedLoader(),
        });

        hlsRef.current = hls;

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
                console.error("[VideoLiveReplaysPlayer] Fatal HLS error:", data);
                break;
            }
          }
        });

        hls.loadSource(videoUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS
        video.src = videoUrl;
      }

      return () => {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }, [videoUrl]);

    // Add native touch event listeners to progress bar
    useEffect(() => {
      const progressBar = progressBarRef.current;
      if (progressBar) {
        progressBar.addEventListener("touchstart", handleNativeTouchStart, { passive: false });
        progressBar.addEventListener("touchmove", handleNativeTouchMove, { passive: false });
        progressBar.addEventListener("touchend", handleNativeTouchEnd, { passive: false });

        return () => {
          progressBar.removeEventListener("touchstart", handleNativeTouchStart);
          progressBar.removeEventListener("touchmove", handleNativeTouchMove);
          progressBar.removeEventListener("touchend", handleNativeTouchEnd);
        };
      }
    }, [isDragging, duration]);

    return (
      <div className="video-live-replays-player" onClick={handleVideoClick}>
        <video ref={videoRef} className="replay-video" playsInline />

        <div
          className="center-play-button"
          onClick={togglePlay}
          style={{
            opacity: showControls ? 1 : 0,
            pointerEvents: showControls ? "auto" : "none",
          }}
        >
          <img src={isPlaying ? ReplayPauseIcon : ReplayPlayIcon} alt={isPlaying ? "pause" : "play"} />
        </div>

        <div
          className="controls"
          style={{
            opacity: showControls ? 1 : 0,
            pointerEvents: showControls ? "auto" : "none",
          }}
        >
          <div className="progress-bar" ref={progressBarRef} onClick={seekVideo} onMouseDown={handleProgressMouseDown}>
            <div className="progress" style={{ width: `${progress}%` }}></div>
            <div
              className="progress-handle"
              style={{
                left: `${progress}%`,
                opacity: isDragging ? 1 : 0,
              }}
            />
            {isDragging && duration > 0 && (
              <div className="time-tooltip" style={{ left: `${progress}%` }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

VideoLiveReplaysPlayer.displayName = "VideoLiveReplaysPlayer";

export default VideoLiveReplaysPlayer;
